import { useEffect, useState } from 'react'
import { coursesAPI } from '../../services/api'
import type { QuizResult, QuizView } from '../../types'
import { apiErrorMessage } from '../../utils/apiError'

const attemptsLine = (quiz: QuizView): string =>
  quiz.max_attempts
    ? `${quiz.attempts_used} of ${quiz.max_attempts} attempts used`
    : `${quiz.attempts_used} ${quiz.attempts_used === 1 ? 'attempt' : 'attempts'} so far`

/**
 * A quiz lesson for a student: the questions (never the answers until
 * they've submitted), marked at once with the right answers and why.
 * Passing marks the lesson done (onPassed).
 */
const QuizPanel = ({ quizId, onPassed }: { quizId: number; onPassed: () => void }) => {
  const [quiz, setQuiz] = useState<QuizView | null>(null)
  const [answers, setAnswers] = useState<Record<number, number>>({})
  const [result, setResult] = useState<QuizResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    coursesAPI
      .quiz(quizId)
      .then(setQuiz)
      .catch((e) => setError(apiErrorMessage(e, "Couldn't load the quiz.")))
  }, [quizId])

  if (!quiz) return error ? <p className="text-sm text-amber-300">{error}</p> : null

  const outOfAttempts = quiz.max_attempts > 0 && quiz.attempts_used >= quiz.max_attempts
  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const marked = await coursesAPI.submitQuiz(quiz.quiz_id, answers)
      setResult(marked)
      setQuiz({
        ...quiz,
        attempts_used: marked.attempts_used,
        passed: quiz.passed || marked.passed,
        best_percentage: Math.max(quiz.best_percentage ?? 0, marked.percentage),
      })
      if (marked.passed) onPassed()
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't mark the quiz."))
    } finally {
      setBusy(false)
    }
  }
  const retry = () => {
    setResult(null)
    setAnswers({})
  }
  const marks = result ? Object.fromEntries(result.results.map((r) => [r.question_id, r])) : {}

  return (
    <section className="space-y-4" data-testid="quiz-panel" aria-label="Quiz">
      <p className="text-sm text-gray-400" data-testid="quiz-status">
        Pass mark {quiz.passing_score}% · {attemptsLine(quiz)}
        {quiz.passed ? ' · Passed' : quiz.best_percentage !== null ? ` · Best ${quiz.best_percentage}%` : ''}
      </p>
      {quiz.questions.length === 0 ? (
        <p className="text-sm text-gray-400">This quiz has no questions yet.</p>
      ) : (
        <ol className="space-y-4">
          {quiz.questions.map((q, n) => {
            const mark = marks[q.id]
            return (
              <li key={q.id} className="rounded-xl border border-terminal-border bg-terminal-surface p-4">
                <fieldset disabled={Boolean(result)}>
                  <legend className="font-medium text-gray-100">
                    {n + 1}. {q.question_text}
                  </legend>
                  <div className="mt-2 space-y-1">
                    {q.options.map((option, i) => (
                      <label
                        key={i}
                        className={`flex items-center gap-2 rounded px-2 py-1 text-sm ${
                          mark && i === mark.correct_option ? 'bg-success-500/20 text-gray-100' : 'text-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`q-${q.id}`}
                          checked={answers[q.id] === i}
                          onChange={() => setAnswers((a) => ({ ...a, [q.id]: i }))}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </fieldset>
                {mark && (
                  <p className={`mt-2 text-sm ${mark.correct ? 'text-success-500' : 'text-amber-300'}`} data-testid="quiz-mark">
                    {mark.correct ? 'Right.' : 'Not quite.'} {mark.explanation || ''}
                  </p>
                )}
              </li>
            )
          })}
        </ol>
      )}
      {result ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className={`text-sm font-medium ${result.passed ? 'text-success-500' : 'text-amber-300'}`} role="status">
            {result.percentage}%: {result.passed ? 'passed, lesson done.' : `the pass mark is ${quiz.passing_score}%.`}
          </p>
          {!result.passed && !outOfAttempts && (
            <button type="button" onClick={retry} className="rounded-md border border-terminal-border px-3 py-1.5 text-sm text-gray-100">
              Try again
            </button>
          )}
        </div>
      ) : (
        quiz.questions.length > 0 &&
        (outOfAttempts && !quiz.passed ? (
          <p className="text-sm text-gray-400">You&rsquo;ve used every attempt at this quiz.</p>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={busy || Object.keys(answers).length === 0}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Marking…' : 'Submit answers'}
          </button>
        ))
      )}
      {error && (
        <p role="alert" className="text-sm text-amber-300">
          {error}
        </p>
      )}
    </section>
  )
}

export default QuizPanel
